<script setup>
import { ref, computed } from 'vue';
import { useForm, router, usePage } from '@inertiajs/vue3';
import { ShieldCheck, MessageSquare, ArrowRight, UserCheck, ArrowLeft, Zap, Smartphone, CreditCard, AlertTriangle } from '@lucide/vue';
import AppLogo from '@/Components/AppLogo.vue';
import Button from '@/Components/Button.vue';
import InputField from '@/Components/InputField.vue';
import AlertBanner from '@/Components/AlertBanner.vue';

defineProps({
  errors: Object,
  flash: Object,
});

const page = usePage();
const authMethods = computed(() => page.props.auth_methods || { google_enabled: false, whatsapp_enabled: true, guest_enabled: true });

const activeTab = ref('options'); // 'options' | 'whatsapp'
const step = ref('phone'); // 'phone' | 'otp'

const form = useForm({
  phone_number: '',
  otp: '',
});

const cooldownSeconds = ref(0);
const isSubmitting = ref(false);
const errorMessage = ref('');
const statusMessage = ref('');

let timer = null;

function startCooldown(seconds) {
  cooldownSeconds.value = seconds;
  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    if (cooldownSeconds.value > 0) {
      cooldownSeconds.value--;
    } else {
      clearInterval(timer);
    }
  }, 1000);
}

function sendOtp() {
  isSubmitting.value = true;
  errorMessage.value = '';
  statusMessage.value = '';

  window.axios.post(route('auth.whatsapp.send'), {
    phone_number: form.phone_number,
  }).then((response) => {
    step.value = 'otp';
    statusMessage.value = response.data.message || 'Kode OTP telah dikirim ke nomor WhatsApp Anda.';
    startCooldown(response.data.cooldown_seconds || 60);
  }).catch((error) => {
    errorMessage.value = error.response?.data?.message || 'Gagal mengirim OTP. Periksa nomor Anda.';
  }).finally(() => {
    isSubmitting.value = false;
  });
}

function verifyOtp() {
  isSubmitting.value = true;
  errorMessage.value = '';

  window.axios.post(route('auth.whatsapp.verify'), {
    phone_number: form.phone_number,
    otp: form.otp,
  }).then((response) => {
    window.location.href = response.data.redirect || route('dashboard');
  }).catch((error) => {
    errorMessage.value = error.response?.data?.message || 'Kode OTP tidak valid atau telah kadaluarsa.';
  }).finally(() => {
    isSubmitting.value = false;
  });
}

function enterGuestMode() {
  router.post(route('auth.guest'));
}
</script>

<template>
  <div class="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between p-4 sm:p-6 md:p-8 select-none">
    <!-- Header Logo -->
    <header class="max-w-6xl w-full mx-auto py-2 flex items-center justify-between">
      <AppLogo size="md" light />
      <span class="text-xs font-semibold px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
        Versi 1.0
      </span>
    </header>

    <!-- Main Content Container -->
    <main class="max-w-5xl w-full mx-auto my-auto py-6 grid lg:grid-cols-12 gap-8 items-center">
      <!-- Left Hero Banner (Desktop) -->
      <div class="lg:col-span-6 space-y-6 hidden lg:block pr-4">
        <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold">
          <Zap class="w-3.5 h-3.5 text-indigo-600" />
          <span>Solusi PPOB Terpercaya & Cepat</span>
        </div>

        <h1 class="text-3xl xl:text-4xl font-black text-slate-900 tracking-tight leading-tight">
          Pulsa, Tagihan & Produk Digital dalam Satu Platform
        </h1>

        <p class="text-slate-600 text-sm leading-relaxed max-w-md">
          Kelola transaksi PPOB dengan aman untuk kebutuhan pribadi, konter pulsa, kasir, dan reseller di seluruh Indonesia.
        </p>

        <!-- Feature List -->
        <div class="space-y-3 pt-2">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <Smartphone class="w-4 h-4" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-800">Pulsa & Paket Data Lengkap</h4>
              <p class="text-[11px] text-slate-500">Semua operator seluler Indonesia dengan harga kompetitif.</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
              <CreditCard class="w-4 h-4" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-800">Pembayaran PLN & Tagihan</h4>
              <p class="text-[11px] text-slate-500">Token listrik, air PDAM, BPJS, dan voucher game.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Login Form Card -->
      <div class="lg:col-span-6 flex justify-center lg:justify-end">
        <div class="w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 border border-slate-200/90 shadow-xl shadow-slate-200/60">
          <div class="mb-6">
            <h2 class="text-xl font-bold text-slate-900 tracking-tight">Selamat Datang</h2>
            <p class="text-xs text-slate-500 mt-1 leading-relaxed">
              Masuk atau jelajahi fitur Manza PPOB untuk memulai.
            </p>
          </div>

          <!-- Flash & Status Banners -->
          <div class="space-y-3 mb-6">
            <AlertBanner v-if="flash?.status || statusMessage" type="success" :message="flash?.status || statusMessage" />
            <AlertBanner v-if="flash?.error || errorMessage || errors?.auth" type="error" :message="flash?.error || errorMessage || errors?.auth" />
          </div>

          <!-- Tab Options State -->
          <div v-if="activeTab === 'options'" class="space-y-4">
            <!-- 1. Google Auth Button -->
            <template v-if="authMethods.google_enabled">
              <a
                :href="route('auth.google')"
                class="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-800 font-semibold text-sm transition border border-slate-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300 min-h-[44px]"
              >
                <svg class="w-5 h-5 shrink-0" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.6l3.1-3.1C17.3 1.7 14.8 1 12 1 7.5 1 3.7 3.6 1.9 7.3l3.7 2.9C6.5 7.2 9 5 12 5z"/>
                  <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.7-.2-2.3H12v4.6h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-8.9z"/>
                  <path fill="#FBBC05" d="M5.6 14.8c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3L1.9 7.3C.7 9.2 0 10.5 0 12s.7 2.8 1.9 4.7l3.7-2.9z"/>
                  <path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3 0-5.5-2.2-6.4-5.2L1.9 16c1.8 3.7 5.6 7 10.1 7z"/>
                </svg>
                <span>Lanjutkan dengan Google</span>
              </a>
            </template>
            <template v-else>
              <div class="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 text-slate-400 font-medium text-xs border border-slate-200 cursor-not-allowed">
                <div class="flex items-center gap-2">
                  <AlertTriangle class="w-4 h-4 text-amber-500" />
                  <span>Google Auth Belum Dikonfigurasi</span>
                </div>
                <span class="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded font-bold">Offline</span>
              </div>
            </template>

            <!-- 2. WhatsApp Login Button -->
            <Button
              @click="activeTab = 'whatsapp'"
              variant="secondary"
              fullWidth
            >
              <MessageSquare class="w-4 h-4" />
              <span>Masuk dengan WhatsApp</span>
            </Button>

            <!-- Divider -->
            <div class="relative my-5">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-slate-200"></div>
              </div>
              <div class="relative flex justify-center text-xs uppercase">
                <span class="bg-white px-3 text-slate-400 font-semibold tracking-wider">Atau</span>
              </div>
            </div>

            <!-- 3. Guest Mode Card -->
            <div class="rounded-xl border border-slate-200 bg-slate-50/70 p-4 transition hover:border-slate-300">
              <div class="flex items-start gap-3">
                <div class="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 shrink-0 mt-0.5 shadow-sm">
                  <UserCheck class="w-4 h-4" />
                </div>
                <div class="flex-1">
                  <h3 class="text-xs font-bold text-slate-800">Jelajahi sebagai Tamu</h3>
                  <p class="text-[11px] text-slate-500 mt-1 leading-relaxed">
                    Lihat produk dan fitur tanpa membuat akun. Transaksi dan fitur akun akan dinonaktifkan.
                  </p>
                  <button
                    @click="enterGuestMode"
                    type="button"
                    class="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition"
                  >
                    <span>Masuk Mode Tamu</span>
                    <ArrowRight class="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- WhatsApp Flow Tab -->
          <div v-if="activeTab === 'whatsapp'" class="space-y-4">
            <button
              @click="activeTab = 'options'; step = 'phone';"
              type="button"
              class="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 transition font-medium mb-1"
            >
              <ArrowLeft class="w-3.5 h-3.5" />
              <span>Kembali ke pilihan login</span>
            </button>

            <!-- Step 1: Phone Number -->
            <form v-if="step === 'phone'" @submit.prevent="sendOtp" class="space-y-4">
              <InputField
                v-model="form.phone_number"
                label="Nomor WhatsApp"
                placeholder="081234567890"
                required
              />

              <p class="text-[11px] text-slate-500 leading-normal">
                Kode verifikasi OTP 6 digit akan dikirim melalui pesan WhatsApp ke nomor di atas.
              </p>

              <Button
                type="submit"
                variant="secondary"
                fullWidth
                :isLoading="isSubmitting"
              >
                Kirim Kode OTP
              </Button>
            </form>

            <!-- Step 2: OTP Verification -->
            <form v-if="step === 'otp'" @submit.prevent="verifyOtp" class="space-y-4">
              <div>
                <label class="block text-xs font-semibold text-slate-700 mb-1">Kode OTP 6 Digit</label>
                <input
                  v-model="form.otp"
                  type="text"
                  maxlength="6"
                  placeholder="123456"
                  required
                  class="w-full py-3.5 rounded-xl border border-slate-300 text-center tracking-[0.5em] text-xl font-bold font-mono text-slate-900 bg-white focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 min-h-[44px]"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                :isLoading="isSubmitting"
              >
                Verifikasi & Masuk
              </Button>

              <div class="flex items-center justify-between text-xs pt-1">
                <button
                  type="button"
                  @click="step = 'phone'"
                  class="text-slate-500 hover:text-slate-800 underline"
                >
                  Ubah Nomor
                </button>

                <button
                  v-if="cooldownSeconds === 0"
                  type="button"
                  @click="sendOtp"
                  class="text-indigo-600 font-bold hover:underline"
                >
                  Kirim Ulang OTP
                </button>

                <span v-else class="text-slate-400">
                  Kirim ulang dalam {{ cooldownSeconds }}s
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="max-w-6xl w-full mx-auto py-3 border-t border-slate-200/80 text-center text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-2">
      <div>&copy; 2026 Manza PPOB. Seluruh hak cipta dilindungi.</div>
      <div class="flex items-center gap-4 text-[11px]">
        <span class="hover:underline cursor-pointer">Syarat & Ketentuan</span>
        <span>&bull;</span>
        <span class="hover:underline cursor-pointer">Kebijakan Privasi</span>
      </div>
    </footer>
  </div>
</template>
